import React, { useEffect, useState, useRef, useCallback } from 'react';
import classNames from 'classnames';
import { head } from 'lodash/fp';
import request from 'utils/request_ajax';
import { routes } from 'constants/config';
import FileUpload from './FileUpload';
import UploadStatus from './UploadStatus';
import MessageBanner, { BANNER_TYPES } from './MessageBanner';

const initialMessage = t('questions.types.elearning.file_upload.upload_file');
const fileProcessingMessage = t(
  'questions.types.elearning.file_upload.package_processing',
);
const fileUploadedMessage = t(
  'questions.types.elearning.file_upload.package_processed',
);

const { ERROR, WARNING } = BANNER_TYPES;

const ElearningFileUploader = ({
  setUploadSuccessful,
  scormId,
  canUploadFile,
  filename,
  isUploading,
}) => {
  const [unmounted, setUnmounted] = useState(false);
  const [uploading, setUploading] = useState(isUploading);
  const [isFileUploaded, setIsFileUploaded] = useState(!!filename);
  const [uploadFileName, setUploadFileName] = useState(filename);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [canReplaceFile, setCanReplaceFile] = useState(canUploadFile);
  const [processingProgress, setProcessingProgress] = useState(1);
  const [message, setMessage] = useState(
    filename ? fileUploadedMessage : initialMessage,
  );
  const [error, setError] = useState(null);
  const fileUploadRef = useRef();

  useEffect(() => {
    return () => {
      setUnmounted(true);
    };
  }, []);

  const isFileValid = file => {
    return /\.zip/i.test(file.name);
  };

  const updateScormMetadata = useCallback(
    (rusticiImportJobId, uploadPending, fileName) => {
      setMessage(fileProcessingMessage);
      request.putJSON(routes.SCORM_UPDATE_METADATA(scormId), {
        import_job_id: rusticiImportJobId,
        upload_pending: uploadPending,
        filename: fileName,
      });
    },
    [scormId],
  );

  const handleUploadFail = useCallback(
    response => {
      if (!unmounted) {
        const uploadErrorMessage =
          response.message ||
          response.responseText ||
          t('questions.types.elearning.file_upload.error.upload_failed');
        setError({
          message: uploadErrorMessage,
          type: ERROR,
        });
        setUploading(false);
        setIsFileUploaded(false);
        setUploadProgress(0);
        setProcessingProgress(1);
        setMessage(initialMessage);
        setCanReplaceFile(canUploadFile);
        updateScormMetadata(null, false, null);
      }
    },
    [canUploadFile, unmounted, updateScormMetadata],
  );

  const handleScormSuccess = useCallback(
    response => {
      if (!unmounted && response.successful) {
        const { warning } = response;
        setUploadFileName(response.file_name);
        setUploadSuccessful(response.version);
        setUploading(false);
        setIsFileUploaded(true);
        setMessage(fileUploadedMessage);
        if (warning) {
          setError({
            message:
              warning +
              '. The display of your eLearning content may be affected.',
            type: WARNING,
          });
        }
        setCanReplaceFile(false);
      } else {
        handleUploadFail(response);
      }
    },
    [handleUploadFail, setUploadSuccessful, unmounted],
  );

  const statusCheck = useCallback(() => {
    request
      .get(routes.SCORM_STATUS(scormId), {
        updating: true,
      })
      .then(response => {
        switch (response.status) {
          case 'COMPLETE':
            setProcessingProgress(50);
            setTimeout(() => handleScormSuccess(response), 2000);
            break;
          case 'RUNNING':
            setTimeout(() => statusCheck(), 1000);
            setProcessingProgress(prevState => Math.min(40, prevState + 1));
            setMessage(fileProcessingMessage);
            break;
          default:
            handleUploadFail(response);
        }
      })
      .fail(() => {
        const failureMessage = {
          error: t(
            'questions.types.elearning.file_upload.error.connection_lost',
          ),
        };
        handleUploadFail(failureMessage);
      });
  }, [handleScormSuccess, handleUploadFail, scormId]);

  const uploadScorm = file => {
    request
      .get(routes.SCORM_UPLOAD_CREDENTIALS)
      .then(credentials => {
        const {
          tenant_name: engineTenantName,
          token,
          base_path: basePath,
        } = credentials;
        const url = `${basePath}/courses/importJobs/upload?courseId=${scormId}&mayCreateNewVersion=true`;
        const formdata = new FormData();
        formdata.append('file', file, file.name);
        request
          .post(url, formdata, {
            headers: {
              Authorization: `Bearer ${token}`,
              engineTenantName,
            },
            contentType: false,
            processData: false,
          })
          .then(response => {
            updateScormMetadata(response.result, true, file.name);
            statusCheck();
          })
          .catch(err => handleUploadFail(err));
      })
      .catch(err => handleUploadFail(err));
  };

  const fileUpload = (e, files) => {
    const originalFile = head(files || e.target.files);
    const sanitizedFilename = originalFile.name.replace(/[^\w.]/gi, '');
    const file = new File([originalFile], sanitizedFilename, {
      type: originalFile.type,
    });

    setError(null);
    if (isFileValid(file)) {
      setIsFileUploaded(false);
      setUploading(true);
      setMessage(t('questions.types.elearning.file_upload.package_uploading'));
      uploadScorm(file);
    } else {
      setError({
        message: t('questions.types.elearning.file_upload.error.file_type'),
        type: ERROR,
      });
      if (e) e.target.value = '';
    }
  };

  useEffect(() => {
    if (isUploading) {
      setMessage(fileProcessingMessage);
      setIsFileUploaded(false);
      statusCheck();
    }
  }, [isUploading, statusCheck]);

  const uploaderClasses = classNames('ElearningFileUpload', {
    'ElearningFileUpload--uploading': uploading,
    'ElearningFileUpload--success': isFileUploaded,
  });
  const showUploadStatus = uploading || isFileUploaded;

  return (
    <div>
      {error && <MessageBanner message={error.message} type={error.type} />}
      <div className={uploaderClasses}>
        {showUploadStatus ? (
          <UploadStatus
            uploading={uploading}
            isFileUploaded={isFileUploaded}
            message={message}
            uploadFileName={uploadFileName}
            canReplaceFile={canReplaceFile}
            fileUploadRef={fileUploadRef}
            uploadProgress={uploadProgress}
            processingProgress={processingProgress}
          />
        ) : (
          <FileUpload
            fileUpload={fileUpload}
            fileUploadRef={fileUploadRef}
            message={message}
          />
        )}
      </div>
      <input
        className="ElearningFileUpload-upload-btn"
        ref={fileUploadRef}
        type="file"
        onChange={fileUpload}
        disabled={uploading}
        tabIndex="-1"
      />
    </div>
  );
};

export default ElearningFileUploader;
